import { motion } from 'framer-motion';
import { Clock, Package, CheckCircle, User, MessageSquare, Upload, XCircle, AlertTriangle, Ban } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { OrderStatusTracker } from '../OrderStatusTracker';

export function OrdersTab({ orders, pendingOrders, activeOrders, completedOrders, statusConfig, handleAcceptOrder, handleDenyOrder, handleDeliverClick, handleCancelOrder, handleApproveCancelOrder, handleDenyCancelOrder, setSelectedOrderForDispute, setDisputeDialogOpen }) {
  const cancelRequestOrders = orders.filter(o => o.cancel_requested && ['paid', 'accepted', 'delivered'].includes(o.status));

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-surface border border-[#262626] rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center"><Clock className="w-6 h-6 text-blue-500" /></div>
            <div><p className="text-2xl font-mono font-bold">{pendingOrders.length}</p><p className="text-sm text-[#A1A1AA]">Pending Requests</p></div>
          </div>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center"><Package className="w-6 h-6 text-purple-500" /></div>
            <div><p className="text-2xl font-mono font-bold">{activeOrders.length}</p><p className="text-sm text-[#A1A1AA]">In Progress</p></div>
          </div>
        </div>
        <div className="bg-surface border border-[#262626] rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-500" /></div>
            <div><p className="text-2xl font-mono font-bold">{completedOrders.length}</p><p className="text-sm text-[#A1A1AA]">Completed</p></div>
          </div>
        </div>
      </div>

      {/* Cancellation Requests */}
      {cancelRequestOrders.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />Cancellation Requests</h2>
          <div className="space-y-4">
            {cancelRequestOrders.map((order) => (
              <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-surface border border-red-500/30 rounded-xl p-6" data-testid={`cancel-request-${order.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center"><Ban className="w-5 h-5 text-red-400" /></div>
                    <div><h3 className="font-bold">{order.user_name}</h3><p className="text-sm text-red-400">Requested cancellation for #{order.id.slice(0, 8)}</p></div>
                  </div>
                  <span className="font-mono text-lg text-[#A1A1AA]">${order.price.toFixed(2)}</span>
                </div>
                <p className="text-sm text-[#A1A1AA] mb-4">If approved, a full refund of ${order.price.toFixed(2)} will be issued to the buyer.</p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => handleApproveCancelOrder(order.id)} className="bg-red-500 hover:bg-red-600 text-white text-sm" data-testid={`approve-cancel-${order.id}`}><CheckCircle className="w-4 h-4 mr-2" />Approve & Refund</Button>
                  <Button onClick={() => handleDenyCancelOrder(order.id)} variant="outline" className="text-sm border-[#333]" data-testid={`deny-cancel-${order.id}`}><XCircle className="w-4 h-4 mr-2" />Deny Request</Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {pendingOrders.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />Pending Requests</h2>
          <div className="space-y-4">
            {pendingOrders.map((order) => (
              <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-surface border border-[#262626] rounded-xl p-6" data-testid={`pending-order-${order.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-surface-highlight rounded-full flex items-center justify-center"><User className="w-5 h-5" /></div>
                    <div><h3 className="font-bold">{order.user_name}</h3><p className="text-sm text-[#A1A1AA]">{order.user_email}</p></div>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-lg text-lime">${order.price.toFixed(2)}</span>
                    <p className="text-xs text-[#A1A1AA]">Your earnings: ${(order.artist_earnings || order.price * 0.9).toFixed(2)}</p>
                  </div>
                </div>
                <div className="mb-4 p-4 bg-surface-highlight rounded-lg space-y-2">
                  {order.song_title && (
                    <div className="flex items-start gap-2"><span className="text-xs text-[#666] font-medium mt-0.5">SONG:</span><p className="text-sm font-medium text-lime">{order.song_title}</p></div>
                  )}
                  <div className="flex items-start gap-2"><MessageSquare className="w-4 h-4 mt-0.5 text-[#666]" /><p className="text-sm">{order.message}</p></div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => handleAcceptOrder(order.id)} className="btn-primary text-sm" data-testid={`accept-${order.id}`}><CheckCircle className="w-4 h-4 mr-2" />Accept Request</Button>
                  <Button onClick={() => handleDenyOrder(order.id)} variant="outline" className="text-sm border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`deny-${order.id}`}><XCircle className="w-4 h-4 mr-2" />Deny</Button>
                  {['paid', 'accepted', 'delivered'].includes(order.status) && !order.has_dispute && (
                    <Button onClick={() => { setSelectedOrderForDispute(order); setDisputeDialogOpen(true); }} variant="ghost" className="text-sm text-[#888] hover:text-red-400" data-testid={`report-issue-pending-${order.id}`}><AlertTriangle className="w-4 h-4 mr-2" />Report Issue</Button>
                  )}
                  {order.has_dispute && <Badge className="bg-red-500/10 text-red-400 border-0"><AlertTriangle className="w-3 h-3 mr-1" />Dispute {order.dispute_status || 'Open'}</Badge>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {activeOrders.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold mb-4">In Progress</h2>
          <div className="space-y-4">
            {activeOrders.map((order) => {
              const deadline = new Date(order.deadline);
              const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-surface border border-[#262626] rounded-xl p-6" data-testid={`active-order-${order.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div><h3 className="font-bold">{order.user_name}</h3><p className="text-sm text-[#A1A1AA] font-mono">#{order.id.slice(0, 8)}</p></div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-lime">${(order.artist_earnings || order.price * 0.9).toFixed(2)}</span>
                      <div className="flex items-center gap-2"><Clock className={`w-4 h-4 ${daysLeft <= 7 ? 'text-red-400' : 'text-[#A1A1AA]'}`} /><span className={`text-sm ${daysLeft <= 7 ? 'text-red-400' : 'text-[#A1A1AA]'}`}>{daysLeft} days left</span></div>
                    </div>
                  </div>
                  <OrderStatusTracker status={order.status} />
                  {order.cancel_requested && (
                    <div className="mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20 flex items-center gap-2">
                      <Ban className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <p className="text-sm text-red-400">Buyer has requested cancellation</p>
                    </div>
                  )}
                  <div className="mb-4 p-4 bg-surface-highlight rounded-lg space-y-2">
                    {order.song_title && (
                      <p className="text-sm"><span className="text-xs text-[#666] font-medium">SONG:</span> <span className="font-medium text-lime">{order.song_title}</span></p>
                    )}
                    <p className="text-sm">{order.message}</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => handleDeliverClick(order)} className="btn-primary text-sm" data-testid={`deliver-${order.id}`}><Upload className="w-4 h-4 mr-2" />Upload & Deliver</Button>
                    <Button onClick={() => handleCancelOrder(order.id)} variant="outline" className="text-sm border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`cancel-order-${order.id}`}><Ban className="w-4 h-4 mr-2" />Cancel Order</Button>
                    {['paid', 'accepted', 'delivered'].includes(order.status) && !order.has_dispute && (
                      <Button onClick={() => { setSelectedOrderForDispute(order); setDisputeDialogOpen(true); }} variant="ghost" className="text-sm text-[#888] hover:text-red-400" data-testid={`report-issue-active-${order.id}`}><AlertTriangle className="w-4 h-4 mr-2" />Report Issue</Button>
                    )}
                    {order.has_dispute && <Badge className="bg-red-500/10 text-red-400 border-0"><AlertTriangle className="w-3 h-3 mr-1" />Dispute {order.dispute_status || 'Open'}</Badge>}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {completedOrders.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-4">Completed</h2>
          <div className="space-y-4">
            {completedOrders.map((order) => {
              const status = statusConfig[order.status];
              return (
                <div key={order.id} className="bg-surface border border-[#262626] rounded-xl p-6" data-testid={`completed-order-${order.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div><h3 className="font-bold">{order.user_name}</h3><p className="text-sm text-[#A1A1AA]">{new Date(order.created_at).toLocaleDateString()}</p></div>
                    <div className="flex items-center gap-3"><span className="font-mono text-lime">${(order.artist_earnings || order.price * 0.9).toFixed(2)}</span><Badge className={`${status?.color || 'bg-[#262626] text-[#A1A1AA]'} border-0`}>{status?.label || order.status}</Badge></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {orders.length === 0 && (
        <div className="text-center py-16 bg-surface rounded-2xl border border-[#262626]"><Package className="w-16 h-16 mx-auto mb-4 text-[#333]" /><h3 className="text-xl font-heading font-bold mb-2">No Orders Yet</h3><p className="text-[#A1A1AA]">When customers place orders, they'll appear here</p></div>
      )}
    </>
  );
}
